import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ShoppingBag } from 'lucide-react';
import { SITE_CONFIG } from '../data/mock';
import { useCart } from '../context/CartContext';
import CartDrawer from './CartDrawer';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const location = useLocation();
  const { itemCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Full Menu', href: '/menu' },
    { label: 'Meal Plan', href: '/meal-plan' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Blog', href: '/blog' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-green-600 text-white text-center py-2 px-4 text-sm font-medium">
        Use code <strong>NINJA</strong> for 20% off your first order at The Prep Kitchen &nbsp;·&nbsp;
        <a href="https://theprepkitchen.com/?ref=NINJA&utm_source=dfwfitmeals" target="_blank" rel="sponsored noopener" className="underline underline-offset-2 font-bold hover:text-green-100 transition-colors">
          Order Now <ArrowUpRight size={12} className="inline" />
        </a>
      </div>

      {/* Main navbar */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 navbar-blur ${
          scrolled
            ? 'bg-white/95 shadow-md border-b border-gray-100'
            : 'bg-white/90 border-b border-gray-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="/logo.png"
                alt="DFW Fit Meals Logo"
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center gap-2">
                <div className="w-9 h-9 bg-green-600 rounded-lg flex items-center justify-center shadow-sm">
                  <span className="text-white font-extrabold text-lg leading-none">D</span>
                </div>
                <div className="flex flex-col leading-none">
                  <span className="font-extrabold text-gray-900 text-lg tracking-tight">DFW Fit</span>
                  <span className="font-semibold text-green-600 text-xs tracking-widest uppercase">Meals</span>
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive(link.href)
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-700 hover:text-green-600 hover:bg-green-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href="tel:2148761653"
                className="text-sm font-semibold text-gray-600 hover:text-green-600 transition-colors"
              >
                {SITE_CONFIG.phone}
              </a>
              <button
                onClick={() => setCartOpen(true)}
                className="relative p-2.5 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors"
                data-testid="navbar-cart-btn"
              >
                <ShoppingBag size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-green-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </button>
              <a
                href="https://theprepkitchen.com/?ref=NINJA&utm_source=dfwfitmeals"
                target="_blank"
                rel="sponsored noopener"
                className="bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-green-700 transition-colors shadow-sm flex items-center gap-1.5"
                data-testid="navbar-order-cta"
              >
                Order Now <ArrowUpRight size={14} />
              </a>
            </div>

            {/* Mobile menu toggle */}
            <div className="flex md:hidden items-center gap-2">
              <button
                onClick={() => setCartOpen(true)}
                className="relative p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid="navbar-cart-btn-mobile"
              >
                <ShoppingBag size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-green-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Toggle menu"
              >
                {isOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                    isActive(link.href)
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-700 hover:text-green-600 hover:bg-green-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-2 pb-1">
                <a
                  href="https://theprepkitchen.com/?ref=NINJA&utm_source=dfwfitmeals"
                  target="_blank"
                  rel="sponsored noopener"
                  className="block w-full text-center bg-green-600 text-white px-5 py-3 rounded-lg text-sm font-bold hover:bg-green-700 transition-colors"
                >
                  Order Now — Use Code NINJA
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
}
